import * as React from "react";
import styled, { css } from "styled-components";
import { Theme } from "../../../theme";
import { useTheme } from "@material-ui/styles";
import { useMediaQuery, Dialog } from "@material-ui/core";
import CameraHeader from "./CameraHeader";
import CameraImage from "./CameraImage";
import CameraMeasurements from "./CameraMeasurements";
import CameraGraph from "./CameraGraph";
import { MeasurementState } from "../../../hooks/useCameras";
import { DeploymentInfo } from "../../../services/cameraService";
import { CameraOptionsHook } from "../../../hooks/useCameraOptions";
import { overallRisk } from "./measurementRisk";
import { useState } from "react";
import useInterval from "../../../hooks/useInterval";
import { RISK_WEIGHTS, MAX_MEASUREMENTS } from "../../../constants/matrix";
import CameraWideImage from "./CameraWideImage";

const OPACITY_ALPHA = "80";
const REDUCED_VALUES_INTERVAL = 15; //seconds

const StyledSection = styled.section`
  display: flex;
  flex-direction: column;
  padding: 8px;
  margin-bottom: 8px;
  border: 1px solid #aaa;
  border-radius: 5px;
  background-color: #fff;

  .cameraCardHeader {
    padding-left: 24px;
    margin-top: -8px;
    margin-left: -8px;
    margin-right: -8px;
    margin-bottom: 8px;
    border-top-left-radius: 5px;
    border-top-right-radius: 5px;
    display: flex;
  }
  .cameraCardContent {
    display: flex;
    flex-direction: row-reverse;
    height: auto;
  }

  .dialog {
    overflow: hidden;
  }

  ${({ theme }: { theme: Theme }) => {
    return css``;
  }};
`;
export interface CameraCardProps {
  deployment: DeploymentInfo;
  liveState: MeasurementState | undefined;
  reducedState: MeasurementState[] | undefined;
  startIndex: number;
  options: CameraOptionsHook["options"];
}

const CameraCard: React.FC<CameraCardProps> = props => {
  const { deployment, liveState, reducedState, options } = props;
  const theme: Theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));

  //Control the playback index
  const [displayIndex, setDisplayIndex] = useState<number>(0);
  const [liveDisplay, setLiveDisplay] = useState<boolean>(true);
  const [displayWideView, setDisplayWideView] = useState<boolean>(false);
  const [play, setPlay] = useState<boolean>(true);

  //Create interval to play through data
  const playSpeed = ~~((REDUCED_VALUES_INTERVAL * 1000) / options.speed);
  useInterval(() => {
    if (reducedState && displayIndex < reducedState.length - 1) {
      setDisplayIndex(displayIndex + 1);
    } else {
      setLiveDisplay(true); //If it reaches the end switch back to live
    }
  }, playSpeed);

  //SET THE CURRENT VISIBLE STATE
  const visibleState = liveDisplay
    ? liveState
    : reducedState
    ? reducedState[displayIndex]
    : //If no live state or reduced states will return undefined
      undefined;

  const visibleMeasurements = {
    density: visibleState ? visibleState.density : 0,
    mood: visibleState ? visibleState.mood : 0,
    flow: visibleState ? visibleState.flow : 0
  };

  //Calculate risk color for header
  const riskColor = visibleState
    ? overallRisk(visibleState, RISK_WEIGHTS).color + OPACITY_ALPHA
    : "#ccc";

  //#0f0 GLITCH with the graph indicator being erased off
  const handleGraphClick = (data: { activeLabel: string }) => {
    // const timeClicked = new Date(data.activeLabel);
    try {
      if (reducedState) {
        const position = reducedState
          .slice(props.startIndex) //Dont read any of the past undefined data
          .map(v => v.timestamp.toISOString())
          .indexOf(data.activeLabel);
        if (position >= 0) {
          setDisplayIndex(position + props.startIndex);
          setLiveDisplay(false);
        }
      }
    } catch (error) {
      console.log("Error with graph click:", error);
    }
  };

  //Handles clicks onto the image
  const handleImageClick = () => {
    console.log("Image clicked");
    setDisplayWideView(!displayWideView);
  };
  const handleWideViewExit = () => {
    setDisplayWideView(false);
  };

  return (
    <>
      <StyledSection theme={theme}>
        <CameraHeader
          name={deployment.deploymentName}
          riskColor={riskColor}
          description={deployment.description}
        ></CameraHeader>
        <div className="cameraCardContent">
          <CameraImage
            showHeatmap={false}
            image={visibleState ? visibleState.image : ""}
            heatmap={visibleState ? visibleState.heatmap : ""}
            handleImageClick={handleImageClick}
          ></CameraImage>
          <CameraMeasurements
            measurements={visibleMeasurements}
            maxMeasurements={MAX_MEASUREMENTS}
            timestamp={visibleState ? visibleState.timestamp : new Date()}
          ></CameraMeasurements>
          {mdUp && (
            <CameraGraph
              data={reducedState ? reducedState.slice(props.startIndex) : []} //Slice to not access past undefined values
              timestamp={visibleState ? visibleState.timestamp : null}
              onGraphClick={handleGraphClick}
            ></CameraGraph>
          )}
        </div>
        {displayWideView && (
          <Dialog
            className="dialog"
            onClose={handleWideViewExit}
            aria-labelledby="simple-dialog-title"
            open={displayWideView}
          >
            <CameraWideImage
              showHeatmap={false}
              image={visibleState ? visibleState.image : ""}
              heatmap={visibleState ? visibleState.heatmap : ""}
            ></CameraWideImage>
          </Dialog>
        )}
      </StyledSection>
    </>
  );
};

export default CameraCard;
